import { computed, makeObservable, override } from 'mobx';
import sortBy from 'lodash/sortBy';

import { MedicalCaseStatus, DiagnosisType } from 'chaika-enums';

import type { DiagnosisParams } from 'chaika-frontend-types/frontend-cms';

import { Diagnosis } from './Diagnosis';
import { List } from '@store/abstract/List';

export interface DiagnosisFilterParams {
    statuses: MedicalCaseStatus[];
    types: DiagnosisType[];
}

export class DiagnosisList extends List<Diagnosis, DiagnosisParams, DiagnosisFilterParams> {
    protected identifiableFieldName = 'medicalCaseId';

    constructor(diagnoses: DiagnosisParams[]) {
        super();
        makeObservable(this);

        this.filterParams = {
            statuses: [],
            types: [],
        };

        this.set(diagnoses || []);
    }

    @computed
    public get hasOpenDiagnosis(): boolean {
        return this.openDiagnosisIds.length > 0;
    }

    @computed
    public get openDiagnosisIds(): string[] {
        const openDiagnoses = this.values.filter(diagnosis => diagnosis.status !== MedicalCaseStatus.Closed);

        return openDiagnoses.map(diagnosis => diagnosis.id);
    }

    public serialize(): DiagnosisParams[] {
        const diagnoses: DiagnosisParams[] = [];

        this.list.forEach(diagnosis => {
            diagnoses.push(diagnosis.serialize());
        });

        return diagnoses;
    }

    @override
    public setFilterParams(params: Partial<DiagnosisFilterParams> = {}): void {
        this.filterParams = { ...this.filterParams, ...params };
    }

    protected create(params: DiagnosisParams): Diagnosis {
        return new Diagnosis(this.fixDiagnosisParams(params));
    }

    protected getOrderedValues(params: Diagnosis[]): Diagnosis[] {
        return sortBy(params, [
            ({ status }) => status !== MedicalCaseStatus.Open,
            ({ type }) => type !== DiagnosisType.Acute,
        ]);
    }

    protected filterValue(value: DiagnosisParams, { statuses, types }: DiagnosisFilterParams): boolean {
        return (
            (statuses && statuses.length > 0 ? statuses.includes(value.status) : true) &&
            (types && types.length > 0 ? types.includes(value.type) : true)
        );
    }

    private fixDiagnosisParams({ id, diagnosis, medicalCaseId, ...params }: DiagnosisParams): DiagnosisParams {
        return {
            medicalCaseId: medicalCaseId ?? id,
            ...(diagnosis || {}),
            ...params,
        };
    }
}
